import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import type { IntakeFormDef } from '../../types/intake';
import type { PublicIntakeSample } from '../../types/intake';
import type { ImportPreview } from '../../services/publicIntakeService';

interface Props {
  open: boolean;
  preview: ImportPreview | null;
  def: IntakeFormDef;
  onCancel: () => void;
  /** The confirmed, fully-mapped result — applied via the normal autosave. */
  onConfirm: (result: { fields: ImportPreview['fields']; samples: PublicIntakeSample[] }) => void;
  /** Confirm button text for N included rows (default "Import N rows"). */
  confirmLabel?: (includedCount: number) => string;
  /** Extra caller context shown under the intro (e.g. the WO upload's "project fields are reference only"). */
  contextNote?: string;
}

const SKIP = '__skip__';

const METHOD_LABEL: Record<string, { text: string; color: 'success' | 'info' | 'warning' }> = {
  astm: { text: 'matched by ASTM code', color: 'success' },
  name: { text: 'matched by name', color: 'info' },
  unresolved: { text: 'needs your answer', color: 'warning' },
};

/**
 * Import confirmation — NOTHING is saved until this dialog is confirmed.
 * For our own workbook it's a quick review (rows + fields). For a legacy
 * KeyLAB file it additionally shows how every data-carrying column mapped
 * to our tests, with an editable picker per column: auto-matches are
 * preselected (override if wrong), unrecognized columns must be answered
 * (pick a test or skip) before Import unlocks. Every sample row has an
 * include-checkbox — handy for the template's built-in example rows.
 */
export const ImportReviewDialog = ({ open, preview, def, onCancel, onConfirm, confirmLabel, contextNote }: Props) => {
  const allTests = useMemo(() => def.categories.flatMap((c) => c.tests), [def]);
  const paramKind = useMemo(
    () => new Map(allTests.map((t) => [t.key.toUpperCase(), t.paramKind])), [allTests],
  );
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [included, setIncluded] = useState<boolean[]>([]);

  useEffect(() => {
    if (!preview) return;
    setMapping(Object.fromEntries(
      preview.columns.map((c) => [c.columnId, c.resolvedTestKey ?? '']),
    ));
    setIncluded(preview.samples.map(() => true));
  }, [preview]);

  if (!preview) return null;
  const isLegacy = preview.source === 'keylab';
  const unanswered = preview.columns.filter((c) => !mapping[c.columnId]);
  const includedCount = included.filter(Boolean).length;

  const handleConfirm = () => {
    const samples: PublicIntakeSample[] = [];
    preview.samples.forEach((s, i) => {
      if (!included[i]) return;
      let tests: PublicIntakeSample['tests'];
      if (isLegacy) {
        // Rebuild from the raw per-column entries + the (possibly edited) mapping.
        const byKey = new Map<string, PublicIntakeSample['tests'][number]>();
        for (const entry of s.entries) {
          const target = mapping[entry.columnId];
          if (!target || target === SKIP) continue;
          const wantsStresses = paramKind.get(target.toUpperCase()) === 'stressesKpa';
          const params = wantsStresses && entry.stressesKpa?.length
            ? { stressesKpa: entry.stressesKpa }
            : entry.paramNote ? { note: entry.paramNote } : null;
          const existing = byKey.get(target.toUpperCase());
          if (existing) {
            existing.quantity += entry.quantity;
            if (params) existing.params = params;
          } else {
            byKey.set(target.toUpperCase(), {
              testKey: target,
              quantity: entry.quantity,
              params,
            });
          }
        }
        tests = [...byKey.values()];
      } else {
        tests = s.tests;
      }
      samples.push({
        locationId: s.locationId, topDepth: s.topDepth, baseDepth: s.baseDepth,
        sampleRef: s.sampleRef, sampleType: s.sampleType, tests,
      });
    });
    onConfirm({ fields: preview.fields, samples });
  };

  const fieldPreview: [string, string | null | undefined][] = [
    ['Project Name', preview.fields.projectName],
    ['Project #', preview.fields.projectId],
    ['Location', preview.fields.projectLocation],
    ['Client', preview.fields.clientName],
    ['Contact', preview.fields.contactName],
    ['Email', preview.fields.contactEmail],
    ['Due date', preview.fields.dueDate],
  ];

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        Review import
        <Chip
          size="small"
          label={isLegacy ? 'Legacy KeyLAB request form' : 'MoftakiLab workbook'}
          color={isLegacy ? 'warning' : 'success'}
          sx={{ fontWeight: 600 }}
        />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Nothing is imported until you confirm. Untick any rows you don't want
          {isLegacy ? ', and check how each column mapped to our tests — you can change any of them.' : '.'}
          {contextNote && <> {contextNote}</>}
        </Typography>

        {/* Parsed project fields */}
        {fieldPreview.some(([, v]) => v) && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1, mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {fieldPreview.map(([label, value]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Column mapping (legacy only) */}
        {isLegacy && preview.columns.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Test column mapping
              {unanswered.length > 0 && (
                <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1, fontWeight: 600 }}>
                  {unanswered.length} column{unanswered.length === 1 ? '' : 's'} need an answer
                </Typography>
              )}
            </Typography>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2, maxHeight: 260 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary' } }}>
                    <TableCell>KeyLAB column</TableCell>
                    <TableCell>Sheet</TableCell>
                    <TableCell>Rows</TableCell>
                    <TableCell>Match</TableCell>
                    <TableCell sx={{ minWidth: 260 }}>Imports as</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.columns.map((c) => {
                    const m = METHOD_LABEL[c.method];
                    const value = mapping[c.columnId] ?? '';
                    return (
                      <TableRow key={c.columnId} sx={value === '' ? { bgcolor: 'rgba(230, 81, 0, 0.06)' } : undefined}>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{c.label}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{c.sheet}</TableCell>
                        <TableCell>{c.dataCount}</TableCell>
                        <TableCell>
                          <Tooltip title={c.method === 'unresolved'
                            ? 'We could not match this column to one of our tests — pick one, or skip it.'
                            : `Auto-${m.text} — change it if it's wrong.`}>
                            <Chip size="small" label={m.text} color={m.color} variant="outlined"
                              sx={{ fontSize: '0.68rem', height: 20 }} />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small" fullWidth displayEmpty
                            value={value}
                            onChange={(e) => setMapping((prev) => ({ ...prev, [c.columnId]: e.target.value }))}
                            sx={{ fontSize: '0.8rem' }}
                          >
                            <MenuItem value="" disabled><em>Choose…</em></MenuItem>
                            <MenuItem value={SKIP}><em>Skip this column</em></MenuItem>
                            {(c.candidates.length > 0 ? c.candidates : allTests).map((t) => (
                              <MenuItem key={t.key} value={t.key}>
                                {t.astm ? `${t.name} (${t.astm})` : t.name}
                              </MenuItem>
                            ))}
                            {c.candidates.length > 0 && allTests
                              .filter((t) => !c.candidates.some((x) => x.key.toUpperCase() === t.key.toUpperCase()))
                              .map((t) => (
                                <MenuItem key={t.key} value={t.key} sx={{ opacity: 0.7 }}>
                                  {t.astm ? `${t.name} (${t.astm})` : t.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Sample rows with include-checkboxes */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Sample rows ({includedCount} of {preview.samples.length} selected)
        </Typography>
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2, maxHeight: 240 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary' } }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={includedCount === preview.samples.length && preview.samples.length > 0}
                    indeterminate={includedCount > 0 && includedCount < preview.samples.length}
                    onChange={(e) => setIncluded(preview.samples.map(() => e.target.checked))}
                  />
                </TableCell>
                <TableCell>Location ID</TableCell>
                <TableCell align="right">Top (m)</TableCell>
                <TableCell align="right">Base (m)</TableCell>
                <TableCell>Sample Ref</TableCell>
                <TableCell>Tests in row</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.samples.map((s, i) => (
                <TableRow key={i} hover sx={!included[i] ? { opacity: 0.45 } : undefined}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small" checked={included[i] ?? true}
                      onChange={(e) => setIncluded((prev) => prev.map((v, j) => (j === i ? e.target.checked : v)))}
                    />
                  </TableCell>
                  <TableCell>{s.locationId ?? '—'}</TableCell>
                  <TableCell align="right">{s.topDepth ?? '—'}</TableCell>
                  <TableCell align="right">{s.baseDepth ?? '—'}</TableCell>
                  <TableCell>{s.sampleRef ?? '—'}</TableCell>
                  <TableCell>
                    {(isLegacy ? s.entries.length : s.tests.length) || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {preview.warnings.length > 0 && (
          <Alert severity="warning" sx={{ maxHeight: 130, overflow: 'auto' }}>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Tooltip title={unanswered.length > 0 ? 'Answer every highlighted column first (map it or skip it).' : ''}>
          <span>
            <Button
              variant="contained" onClick={handleConfirm}
              disabled={unanswered.length > 0 || includedCount === 0}
              sx={{ textTransform: 'none' }}
            >
              {confirmLabel ? confirmLabel(includedCount) : `Import ${includedCount} row${includedCount === 1 ? '' : 's'}`}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};
